import React, { useEffect, useMemo, useState } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Badge, Card, Col, Row, Table, Form, ProgressBar } from "react-bootstrap";
import { Link } from "react-router-dom";
import { getToken } from "../services/authorize";
import axiosInstance from "../services/axiosInterceptor";
import { useLoading } from "../contexts/LoadingContext";
import { getCurrentSemester, getDayNameTHByIndex, formatTimeShort } from "../utils/teachingUtils";
import GuidanceRoomDetail from "./GuidanceRoomDetail";
import { WEEK_META, weekTitle, guidanceStatus, guidanceReason } from "../utils/guidanceWeeks";

const API = process.env.REACT_APP_API;

const STATUS_META = {
  complete: { label: "เข้าครบ", bg: "success" },
  partial: { label: "เข้าไม่ครบ", bg: "warning" },
  none: { label: "ยังไม่เข้าเลย", bg: "danger" },
  no_slot: { label: "ยังไม่กำหนดคาบ", bg: "secondary" },
  na: { label: "ยังไม่ถึงกำหนด", bg: "light" },
};

const TABS = [
  { key: "weeks", label: "รายสัปดาห์" },
  { key: "sessions", label: "กิจกรรมรายครั้ง" },
  { key: "students", label: "รายนักเรียน" },
];

// แถบสัปดาห์ — 1 ช่อง = 1 สัปดาห์ตามวันที่กำหนดคาบไว้ (hover ดูเหตุผล)
const WeekStrip = ({ weeks }) => (
  <div className="d-flex flex-wrap" style={{ gap: 3 }}>
    {weeks.map((w) => (
      <span
        key={w.date}
        title={weekTitle(w)}
        style={{
          width: 12, height: 12, borderRadius: 3,
          background: WEEK_META[w.status].color,
          border: "1px solid rgba(0,0,0,.18)",
        }}
      />
    ))}
  </div>
);

const pct = (part, whole) => (whole > 0 ? Math.round((part / whole) * 100) : 0);

// สัดส่วนการเข้าร่วมของนักเรียน (มา+สาย เทียบกับบันทึกทั้งหมด)
const joinRate = (a) => pct(Number(a.presents) + Number(a.lates), Number(a.records));

const slotLabel = (g) =>
  g ? `${getDayNameTHByIndex(g.teach_day)} คาบ ${g.period_order}` : "—";

export default function GuidanceTracking() {
  const currentSkin = localStorage.getItem("skin-mode") ? "dark" : "";
  const [skin, setSkin] = useState(currentSkin);
  const { setLoading } = useLoading();

  const switchSkin = (s) => {
    const tables = document.getElementsByTagName("table");
    for (const t of tables) {
      const theads = t.querySelectorAll("thead");
      if (s === "dark") {
        for (const th of theads) { th.classList.remove("table-secondary"); th.classList.add("table-primary"); }
        t.classList.add("table-dark");
      } else {
        t.classList.remove("table-dark");
        for (const th of theads) { th.classList.remove("table-primary"); th.classList.add("table-secondary"); }
      }
    }
  };
  useEffect(() => { switchSkin(skin); });

  const [yearList, setYearList] = useState([]);
  const [yearId, setYearId] = useState(null);
  const [semester, setSemester] = useState(getCurrentSemester());
  const [classFilter, setClassFilter] = useState("all");
  const [programFilter, setProgramFilter] = useState("all");
  const [onlyPending, setOnlyPending] = useState(false);
  const [rows, setRows] = useState([]);
  const [semWindow, setSemWindow] = useState(null);
  const [expanded, setExpanded] = useState(null); // r_id
  const [activeTab, setActiveTab] = useState({}); // r_id -> tab key

  useEffect(() => {
    axiosInstance
      .get(`${API}/getStudyYear`, { headers: { authorization: `Bearer ${getToken()}` } })
      .then((res) => {
        const years = res.data || [];
        setYearList(years);
        if (years.length > 0) setYearId(years[0].year_id);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!yearId) return;
    setLoading(true);
    setExpanded(null);
    axiosInstance
      .get(`${API}/getGuidanceOverview/${yearId}/${semester}`, {
        headers: { authorization: `Bearer ${getToken()}` },
      })
      .then((res) => {
        setRows(res.data?.rows || []);
        setSemWindow(res.data?.semWindow || null);
      })
      .catch(() => { setRows([]); setSemWindow(null); })
      .finally(() => setLoading(false));
  }, [yearId, semester]); // eslint-disable-line react-hooks/exhaustive-deps

  const classes = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => { if (!map.has(r.c_id)) map.set(r.c_id, r.c_class); });
    return Array.from(map.entries()).sort((a, b) => a[0] - b[0]);
  }, [rows]);

  const programs = useMemo(
    () => Array.from(new Set(rows.map((r) => r.p_code).filter(Boolean))).sort(),
    [rows]
  );

  const withStatus = useMemo(
    () => rows.map((r) => ({ ...r, status: guidanceStatus(r.guidance) })),
    [rows]
  );

  const filtered = useMemo(() => {
    let list = withStatus;
    if (classFilter !== "all") list = list.filter((r) => String(r.c_id) === classFilter);
    if (programFilter !== "all") list = list.filter((r) => r.p_code === programFilter);
    if (onlyPending) list = list.filter((r) => ["none", "partial", "no_slot"].includes(r.status));
    return list;
  }, [withStatus, classFilter, programFilter, onlyPending]);

  const kpi = useMemo(() => {
    const c = { total: filtered.length, complete: 0, partial: 0, none: 0, no_slot: 0 };
    filtered.forEach((r) => {
      if (r.status === "no_slot") c.no_slot += 1;
      else if (r.status === "none") c.none += 1;
      else if (r.status === "partial") c.partial += 1;
      else if (r.status === "complete") c.complete += 1;
    });
    return c;
  }, [filtered]);

  const kpiCards = [
    { label: "ห้องเรียนทั้งหมด", value: kpi.total, color: "primary", icon: "ri-community-line" },
    { label: "เข้าแนะแนวครบ", value: kpi.complete, color: "success", icon: "ri-checkbox-circle-line" },
    { label: "เข้าไม่ครบ", value: kpi.partial, color: "warning", icon: "ri-error-warning-line" },
    { label: "ยังไม่เข้า / ไม่มีคาบ", value: kpi.none + kpi.no_slot, color: "danger", icon: "ri-close-circle-line" },
  ];

  const roomLabel = (r) => `ม.${r.c_id}/${r.r_no}`;

  const toggleExpand = (r) => {
    setExpanded((prev) => (prev === r.r_id ? null : r.r_id));
  };

  const renderProgress = (g) => {
    if (!g) return <span className="text-muted fs-xs">—</span>;
    if (g.expected_sessions == null) {
      return <span className="fs-sm">{g.total_checks} ครั้ง</span>;
    }
    const p = pct(g.checked_sessions, g.expected_sessions);
    return (
      <div style={{ minWidth: 110 }}>
        <div className="fs-xs mb-1">
          {g.checked_sessions}/{g.expected_sessions} สัปดาห์
        </div>
        <ProgressBar
          now={Math.min(p, 100)}
          variant={p >= 100 ? "success" : p > 0 ? "warning" : "danger"}
          style={{ height: 5 }}
        />
      </div>
    );
  };

  const renderDetail = (r) => {
    const tab = activeTab[r.r_id] || TABS[0].key;
    return (
      <>
        <div className="btn-group btn-group-sm mb-3 flex-wrap">
          {TABS.map((t) => (
            <button
              key={t.key}
              className={`btn btn${tab === t.key ? "" : "-outline"}-primary`}
              onClick={() => setActiveTab((prev) => ({ ...prev, [r.r_id]: t.key }))}
            >{t.label}</button>
          ))}
        </div>
        <GuidanceRoomDetail tab={tab} rId={r.r_id} yearId={yearId} semester={semester} weeks={r.guidance?.weeks} />
      </>
    );
  };

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="#">งานแนะแนว</Link></li>
              <li className="breadcrumb-item active">ติดตามคาบแนะแนว</li>
            </ol>
            <h4 className="main-title mb-0">ติดตามคาบแนะแนว</h4>
          </div>
          <div className="d-flex flex-wrap align-items-center gap-2">
            <select
              className="form-select form-select-sm"
              style={{ minWidth: 140 }}
              value={yearId || ""}
              onChange={(e) => setYearId(e.target.value ? Number(e.target.value) : null)}
            >
              {yearList.map((y) => (
                <option key={y.year_id} value={y.year_id}>ปีการศึกษา {y.year_year}</option>
              ))}
            </select>

            <div className="btn-group btn-group-sm">
              {[["1", "ภาคเรียน 1"], ["2", "ภาคเรียน 2"]].map(([v, label]) => (
                <button
                  key={v}
                  className={`btn btn${semester === v ? "" : "-outline"}-primary`}
                  onClick={() => setSemester(v)}
                >{label}</button>
              ))}
            </div>

            {classes.length > 1 && (
              <select
                className="form-select form-select-sm"
                style={{ minWidth: 130 }}
                value={classFilter}
                onChange={(e) => setClassFilter(e.target.value)}
              >
                <option value="all">ทุกระดับชั้น</option>
                {classes.map(([cId, cClass]) => (
                  <option key={cId} value={String(cId)}>{cClass || `ม.${cId}`}</option>
                ))}
              </select>
            )}

            {programs.length > 1 && (
              <select
                className="form-select form-select-sm"
                style={{ minWidth: 120 }}
                value={programFilter}
                onChange={(e) => setProgramFilter(e.target.value)}
              >
                <option value="all">ทุกหลักสูตร</option>
                {programs.map((p) => (<option key={p} value={p}>{p}</option>))}
              </select>
            )}

            <Form.Check
              type="switch"
              id="only-pending"
              label="เฉพาะห้องที่ยังไม่ครบ"
              className="fs-sm ms-1"
              checked={onlyPending}
              onChange={(e) => setOnlyPending(e.target.checked)}
            />
          </div>
        </div>

        <Row className="g-3 mb-4">
          {kpiCards.map((k) => (
            <Col key={k.label} xs={6} xl={3}>
              <Card className={`card-one border-${k.color} border-start border-4`}>
                <Card.Body className="p-3">
                  <div className={`text-${k.color} mb-1`}><i className={`${k.icon} fs-40 lh-1`}></i></div>
                  <h6 className="fw-semibold text-dark mb-1">{k.label}</h6>
                  <h3 className={`fw-bold text-${k.color} mb-0`}>
                    {k.value} <span className="fs-sm fw-normal text-muted">ห้อง</span>
                  </h3>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        {semWindow && !semWindow.sem_first_check ? (
          <div className="alert alert-info fs-sm py-2">
            <i className="ri-information-line me-1"></i>
            ยังไม่มีการเช็คชื่อคาบแนะแนวในภาคเรียนนี้ — คอลัมน์ “เข้าแล้ว” จะยังไม่มีตัวหาร (จำนวนสัปดาห์ที่ควรเข้า)
          </div>
        ) : (
          <div className="alert alert-light border fs-xs py-2">
            <i className="ri-calendar-check-line me-1"></i>
            สัปดาห์ที่ตรงกับวันหยุด/กิจกรรมใน
            <Link to="/general/calendar" className="mx-1">ปฏิทินปฏิบัติงาน</Link>
            (รายการที่ตั้งค่า “งดการเรียนการสอน”) ถูกหักออกจากตัวหารแล้ว ห้องที่ยังขึ้นว่า
            “เข้าไม่ครบ” คือสัปดาห์ที่โรงเรียนเปิดตามปกติแต่ไม่มีการเช็คชื่อ
          </div>
        )}

        <Card className="card-one">
          <Card.Header className="d-flex flex-wrap align-items-center gap-2">
            <Card.Title as="h6" className="mb-0">การเข้าคาบแนะแนวรายห้อง</Card.Title>
            <Badge bg="secondary" className="ms-1">{filtered.length} ห้อง</Badge>
            <span className="fs-xs text-muted ms-2">คลิกที่แถวเพื่อดูเหตุผลรายสัปดาห์</span>
            <div className="d-flex flex-wrap align-items-center gap-3 ms-auto fs-xs text-muted">
              {["checked", "missed", "no_teaching", "upcoming"].map((k) => (
                <span key={k} className="d-inline-flex align-items-center gap-1">
                  <span style={{
                    width: 12, height: 12, borderRadius: 3,
                    background: WEEK_META[k].color, border: "1px solid rgba(0,0,0,.18)",
                  }} />
                  {WEEK_META[k].label}
                </span>
              ))}
            </div>
          </Card.Header>
          <Card.Body className="p-0">
            {filtered.length === 0 ? (
              <div className="text-center text-muted py-5">
                <i className="ri-inbox-line fs-3 d-block mb-1"></i>ไม่มีข้อมูลตามตัวกรอง
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <Table hover responsive className="mb-0 align-middle">
                  <thead className="table-secondary">
                    <tr>
                      <th style={{ width: 40 }}></th>
                      <th style={{ width: 90 }}>ห้อง</th>
                      <th style={{ minWidth: 150 }}>ครูประจำชั้น</th>
                      <th className="text-center" style={{ width: 50 }}>นร.</th>
                      <th style={{ minWidth: 120 }}>คาบแนะแนว</th>
                      <th style={{ minWidth: 130 }}>เข้าแล้ว</th>
                      <th style={{ minWidth: 130 }}>รายสัปดาห์</th>
                      <th style={{ minWidth: 150 }}>ครั้งล่าสุด</th>
                      <th className="text-center" style={{ width: 90 }}>เข้าร่วม</th>
                      <th className="text-center" style={{ minWidth: 120 }}>สถานะ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((r) => {
                      const g = r.guidance;
                      const meta = STATUS_META[r.status];
                      return (
                        <React.Fragment key={r.r_id}>
                          <tr style={{ cursor: "pointer" }} onClick={() => toggleExpand(r)}>
                            <td className="text-center">
                              <i className={`ri-arrow-${expanded === r.r_id ? "down" : "right"}-s-line`}></i>
                            </td>
                            <td>
                              {roomLabel(r)}
                              {r.p_code && <span className="fs-xs text-muted d-block">{r.p_code}</span>}
                            </td>
                            <td className="fs-sm">
                              {r.advisor_names || <span className="text-muted">ยังไม่กำหนดครูประจำชั้น</span>}
                            </td>
                            <td className="text-center">{r.total_students}</td>
                            <td className="fs-sm">
                              {slotLabel(g)}
                              {g && (
                                <span className="fs-xs text-muted d-block">
                                  {formatTimeShort(g.period_begin)}-{formatTimeShort(g.period_end)}
                                </span>
                              )}
                            </td>
                            <td>{renderProgress(g)}</td>
                            <td>
                              {g?.weeks?.length
                                ? <WeekStrip weeks={g.weeks} />
                                : <span className="text-muted fs-xs">—</span>}
                            </td>
                            <td className="fs-sm">
                              {g?.last_check_date ? (
                                <>
                                  {g.last_check_date}
                                  {g.last_topic && (
                                    <span className="fs-xs text-muted d-block text-truncate" style={{ maxWidth: 200 }}>
                                      {g.last_topic}
                                    </span>
                                  )}
                                </>
                              ) : <span className="text-muted">—</span>}
                            </td>
                            <td className="text-center fs-sm">
                              {r.attendance.records > 0
                                ? `${joinRate(r.attendance)}%`
                                : <span className="text-muted">—</span>}
                            </td>
                            <td className="text-center">
                              <Badge bg={meta.bg} className={`fs-xs${r.status === "na" ? " text-dark" : ""}`}>
                                {meta.label}
                              </Badge>
                              {guidanceReason(g) && (
                                <span className="fs-xs text-muted d-block mt-1">{guidanceReason(g)}</span>
                              )}
                            </td>
                          </tr>
                          {expanded === r.r_id && (
                            <tr>
                              <td colSpan={10} className="bg-body p-3">
                                {renderDetail(r)}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>

        <Footer />
      </div>
    </React.Fragment>
  );
}
